import React from 'react';
import {
  alphabeticalSortingComparator,
  PortalViewModel,
  UpdateSocialButtonOrderRequest,
  AnnouncementCategory,
} from 'stageclip-common';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import isObject from 'lodash/isObject';
import omit from 'lodash/omit';
import orderBy from 'lodash/orderBy';

import { PageTitle } from '../pages/pageTitle';

import * as PermissionsHelper from 'helpers/permissionsHelper';
import * as NotificationHelper from 'helpers/notificationHelper';
import { callApi } from 'helpers/callApi/callApi';
import { SimpleModalId } from 'constants/modalId';
import { NormalizedClient } from 'models/normalized/normalizedClient';
import { PortalFormContainer } from './portalsForm/PortalFormContainer';
import { PortalsTable } from './portalsTable/PortalsTable';
import { SocialMediaForm } from './socialMediaForm/SocialMediaForm';
import {
  getPrefixFromPortalsSubdomain,
  setStateOfSocialMediaButtons,
  OperationType,
  satisfiesSearchQuery,
} from './PortalsPage.utils';
import { FormFieldsTypes } from './portalsForm/PortalForm.types';
import { PortalsPageProps, PortalsPageState, InitialValues } from './PortalsPage.types';
import { PortalsSearch } from './portalsSearch/PortalsSearch';
import { PortalsUploadListFormContainer } from './portalsUploadListForm/PortalsUploadListForm';
import { PortalsUploadListFormDataProps } from 'components/portals/portalsUploadListForm/PortalsUploadListForm.types';
import { PortalsDeleteModal } from './portalsDeleteForm/PortalsDeleteForm';
import { PortalsClearCacheModal } from './portalsClearCacheForm/PortalsClearCacheForm';
import { clearPortalCache } from 'api/portals/portals.actions';

export const DEFAULT_BACKGROUND_COLOR = '#FFFFFF';
const DEFAULT_SEARCH_CLIP_ENABLED = true;
const DEFAULT_VIRTUAL_GRADUATION_MESSAGE =
  '<p>Welcome to your Virtual Celebration Ceremony! Find your ceremony by clicking the playlist button on the top-right of the player.</p>';
const DEFAULT_ANNOUNCEMENT_CATEGORY = AnnouncementCategory.Green;

export class PortalsPage extends React.Component<PortalsPageProps, PortalsPageState> {
  defaultInitialValues: InitialValues = {
    logoBackgroundColor: DEFAULT_BACKGROUND_COLOR,
    clipSearchEnabled: DEFAULT_SEARCH_CLIP_ENABLED,
    customContentBackgroundColor: DEFAULT_BACKGROUND_COLOR,
    virtualGraduationMessage: DEFAULT_VIRTUAL_GRADUATION_MESSAGE,
    announcementCategory: DEFAULT_ANNOUNCEMENT_CATEGORY,
  };

  state: PortalsPageState = {
    portalsList: null,
    visiblePortals: null,
    loading: false,
    error: false,
    initialValues: this.defaultInitialValues,
    socialMediaButtons: null,
    initialSocialMediaButtons: null,
    searchQuery: '',
    id: '',
    isUploadingExcel: false,
  };

  componentDidMount() {
    const { loadClients } = this.props;
    this.fetchPortalsList();
    loadClients(this);
    this.fetchSocialButtonsOrder();
  }

  handleSearchPortal = (searchQuery: string) => this.setState({ searchQuery }, this.onSearch);

  fetchPortalsList = () => {
    const { fetchPortalList } = this.props;

    callApi({
      action: () => {
        this.setState({ loading: true });

        return fetchPortalList();
      },
      onSuccess: ({ payload }) =>
        this.setState({ portalsList: payload, visiblePortals: payload, loading: false, error: false }),
      onFailure: () => this.setState({ portalsList: [], visiblePortals: [], loading: false, error: true }),
    });
  };

  fetchSocialButtonsOrder = () => {
    const { fetchSocialButtons } = this.props;

    callApi({
      action: () => fetchSocialButtons(),
      onSuccess: ({ payload }) => {
        const socialMediaButtons = orderBy(payload, 'order', 'asc');

        this.setState({
          socialMediaButtons,
          initialSocialMediaButtons: socialMediaButtons,
        });
      },
    });
  };

  onAddPortal = (values: FormFieldsTypes) => {
    const { onAddPortal, closeAddPortalModal } = this.props;

    callApi({
      action: () => onAddPortal(values),
      onSuccess: () => {
        closeAddPortalModal();
        this.fetchPortalsList();
        NotificationHelper.success('Clip site added!');
        this.handleSearchPortal('');
      },
      onFailure: ({
        payload: {
          response: {
            errors: { domain },
          },
        },
      }) => {
        if (domain) {
          NotificationHelper.error(domain);
        }
      },
    });
  };

  onEditPortal = (values: FormFieldsTypes) => {
    const { onEditPortal, closeEditPortalModal } = this.props;
    const { id } = this.state;

    callApi({
      action: () => onEditPortal(id, values),
      onSuccess: () => {
        closeEditPortalModal();
        this.fetchPortalsList();
        NotificationHelper.success('Clip site edited!');
        this.handleSearchPortal('');
      },
      onFailure: ({
        payload: {
          response: {
            errors: { domain },
          },
        },
      }) => {
        if (domain) {
          NotificationHelper.error(domain);
        }
      },
    });
  };

  onEditSocialMediaButtonsOrder = (values: UpdateSocialButtonOrderRequest[]) => {
    const { editSocialButtons, closeSocialMediaModal } = this.props;

    callApi({
      action: () => editSocialButtons(values),
      onSuccess: () => {
        closeSocialMediaModal();
        this.fetchSocialButtonsOrder();
        NotificationHelper.success('Order edited!');
      },
    });
  };

  onUploadPortalsList = (values: PortalsUploadListFormDataProps) => {
    const { uploadPortalsList, closePortalsUploadModal } = this.props;

    callApi({
      action: () => {
        this.setState({ isUploadingExcel: true });

        return uploadPortalsList(values);
      },
      onSuccess: () => {
        this.setState({ isUploadingExcel: false });
        closePortalsUploadModal();
        this.fetchPortalsList();
        NotificationHelper.success('Clip sites uploaded!');
        this.handleSearchPortal('');
      },
      onFailure: ({
        payload: {
          response: {
            errors: { domain },
          },
        },
      }) => {
        this.setState({ isUploadingExcel: false });

        if (domain) {
          NotificationHelper.error(domain);
        }
      },
    });
  };

  getClientsOptions = () => {
    const { clients } = this.props;

    return clients
      .sort((a: any, b: any) => alphabeticalSortingComparator(a.name, b.name))
      .map((client: NormalizedClient) => ({ value: client.id, label: client.name }));
  };

  onEditModalOpen = (initialValues: PortalViewModel) => {
    const { openEditPortalModal } = this.props;

    this.setState({
      initialValues: {
        ...omit(initialValues, ['id', 'clientName', 'secondaryColor', 'domain', 'alternativeDomains']),
        domain: getPrefixFromPortalsSubdomain(initialValues.domain),
        alternativeDomains: initialValues.alternativeDomains.map(getPrefixFromPortalsSubdomain),
        useColor: !!initialValues.color,
        useSocialMessagingText: !!initialValues.socialMessagingText,
        virtualGraduationMessage: initialValues.virtualGraduationMessage || DEFAULT_VIRTUAL_GRADUATION_MESSAGE,
        color: initialValues.color || initialValues.secondaryColor,
        customContentBackgroundColor: initialValues.customContentBackgroundColor || DEFAULT_BACKGROUND_COLOR,
        announcementCategory: initialValues.announcementCategory || DEFAULT_ANNOUNCEMENT_CATEGORY,
        logoBackgroundColor: initialValues.logoBackgroundColor || DEFAULT_BACKGROUND_COLOR,
      },
      id: initialValues.id,
    });

    openEditPortalModal();
  };

  onClientChange = (selectedClientId: string) => {
    const { clients } = this.props;

    const clienColor = clients.find(client => client.id === selectedClientId)?.colors.secondary;

    this.setState(prevState => ({ initialValues: { ...prevState.initialValues, color: clienColor } }));
  };

  resetInitialValues = () =>
    this.setState({
      initialValues: this.defaultInitialValues,
      id: '',
    });

  handleOnSocialButtonMove = (id: string, type: OperationType) =>
    this.setState(prevState => setStateOfSocialMediaButtons(prevState, id, type));

  onSearch = () => {
    const { portalsList, searchQuery } = this.state;

    const visiblePortals = portalsList.filter(
      ({ clientName, domain }) =>
        satisfiesSearchQuery(clientName, searchQuery) || satisfiesSearchQuery(domain, searchQuery),
    );

    this.setState({ visiblePortals });
  };

  onDeletePortal = (id: string) => {
    const { deletePortal, closeDeletePortalModal } = this.props;

    callApi({
      action: () => deletePortal(id),
      onSuccess: () => {
        closeDeletePortalModal();
        this.fetchPortalsList();
        NotificationHelper.success('Portal deleted!');
        this.handleSearchPortal('');
      },
    });
  };

  onClearPortalCache = async (portalDomain: string) => {
    const { closeClearPortalCacheModal } = this.props;
    try {
      await clearPortalCache(portalDomain);
      closeClearPortalCacheModal();
      NotificationHelper.success('Cache cleared!');
    } catch (err) {
      NotificationHelper.error('Cannot clear cache!');
    }
  };

  render() {
    const { openDeletePortalModal, openClearPortalCacheModal } = this.props;
    const {
      visiblePortals,
      loading,
      error,
      initialValues,
      socialMediaButtons,
      initialSocialMediaButtons,
      searchQuery,
      isUploadingExcel,
    } = this.state;

    return (
      <>
        <PageTitle displayTitle="Clip Sites">
          {PermissionsHelper.userCan(PermissionsHelper.RestrictedActions.CreatePortal) && (
            <>
              <PortalFormContainer
                form="addPortalModal"
                id={SimpleModalId.AddPortalModal}
                title="New clip site"
                clientsOptions={this.getClientsOptions()}
                onSubmit={this.onAddPortal}
                onClientChange={this.onClientChange}
                initialValues={initialValues}
                resetInitialValues={this.resetInitialValues}
                buttonIcon={faPlus}
              />
              <SocialMediaForm
                initialSocialMediaButtons={initialSocialMediaButtons}
                socialMediaButtons={socialMediaButtons || []}
                onSocialButtonMove={this.handleOnSocialButtonMove}
                form={SimpleModalId.SocialMediaModal}
                onSubmit={this.onEditSocialMediaButtonsOrder}
              />
              <PortalsUploadListFormContainer onUpload={this.onUploadPortalsList} isLoading={isUploadingExcel} />
            </>
          )}
        </PageTitle>
        {error ? (
          <div>Something went wrong</div>
        ) : (
          <>
            <PortalsSearch onChange={this.handleSearchPortal} value={searchQuery} />
            <PortalsTable
              portalsList={visiblePortals}
              loading={loading}
              onRowClick={this.onEditModalOpen}
              onDelete={openDeletePortalModal}
              onClearPortalCache={openClearPortalCacheModal}
            />
          </>
        )}
        {isObject(initialValues) && (
          <PortalFormContainer
            form="editPortalModal"
            id={SimpleModalId.EditPortalModal}
            title="Edit clip site"
            clientsOptions={this.getClientsOptions()}
            onSubmit={this.onEditPortal}
            onClientChange={this.onClientChange}
            initialValues={initialValues}
            resetInitialValues={this.resetInitialValues}
            hideOpenButton
          />
        )}
        <PortalsDeleteModal onSubmit={this.onDeletePortal} />
        <PortalsClearCacheModal onSubmit={this.onClearPortalCache} />
      </>
    );
  }
}
